import { ERROR_TITLE_HU, type ErrorCode, type ProblemDetails } from '@persped/shared';

/**
 * A backend RFC 9457 problem+json hibája tipizálva.
 *
 * A `code` a KONTRAKTUS (ld. `packages/shared/src/errors.ts`) — erre ágazunk
 * el, sosem a szövegre. A megjelenítendő magyar szöveget viszont NEM találjuk
 * ki: a szerver `title`/`detail` mezője már magyar, és tartalék az
 * `ERROR_TITLE_HU`. A `packages/shared` tesztje épp azt bünteti, ha nyers
 * kódnév kerül a felhasználó elé.
 */
export class ApiError extends Error {
  readonly code: ErrorCode | 'NETWORK_ERROR';
  readonly status: number;
  readonly detail?: string;
  readonly correlationId?: string;
  readonly details?: Record<string, unknown>;

  constructor(init: {
    code: ErrorCode | 'NETWORK_ERROR';
    status: number;
    title: string;
    detail?: string;
    correlationId?: string;
    details?: Record<string, unknown>;
  }) {
    super(init.title);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.detail = init.detail;
    this.correlationId = init.correlationId;
    this.details = init.details;
  }

  /** A felhasználónak mutatandó főcím. */
  get titleHu(): string {
    if (this.code === 'NETWORK_ERROR') return this.message;
    return this.message || ERROR_TITLE_HU[this.code];
  }

  /**
   * A `VALIDATION_FAILED` mezőhibái. A backend MAGYAR kulcsokkal adja
   * (`{mezo, uzenet}`, ld. `problem-details.filter.ts`), ezért nem lehet
   * naivan `fieldErrors[].field`-et olvasni.
   */
  get fieldErrors(): Array<{ mezo: string; uzenet: string }> {
    const raw = this.details?.['fieldErrors'];
    return Array.isArray(raw) ? (raw as Array<{ mezo: string; uzenet: string }>) : [];
  }

  static fromProblem(problem: ProblemDetails): ApiError {
    return new ApiError({
      code: problem.code,
      status: problem.status,
      title: problem.title,
      detail: problem.detail,
      correlationId: problem.correlationId,
      details: problem.details,
    });
  }

  /** Hálózati hiba (nem jutott el a szerverig, vagy nem problem+json jött vissza). */
  static network(message: string): ApiError {
    return new ApiError({
      code: 'NETWORK_ERROR',
      status: 0,
      title: message,
    });
  }
}
